import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const HOME_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "ElectricalContractor",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#business`,
      "name": "Done to Power Up (DTPU)",
      "alternateName": "DTPU",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "logo": "https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/9515c99ad4768c15e42e5804f92636ee.jpeg",
      "image": "https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/9515c99ad4768c15e42e5804f92636ee.jpeg",
      "description": "Licensed electrical contractor serving NYC and Westchester County. Commercial electrical contracting, panel upgrades, EV charger installation, smart home wiring and 24/7 emergency electrical service.",
      "telephone": "+16467501474",
      "email": "office@dtpuservices.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "2568 17th Street, Suite 101",
        "addressLocality": "Brooklyn",
        "addressRegion": "NY",
        "postalCode": "11235",
        "addressCountry": "US"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": 40.6501,
        "longitude": -73.9496
      },
      "areaServed": [
        { "@type": "City", "name": "New York City" },
        { "@type": "AdministrativeArea", "name": "Westchester County" },
        { "@type": "City", "name": "Brooklyn" },
        { "@type": "City", "name": "Queens" },
        { "@type": "City", "name": "Manhattan" },
        { "@type": "City", "name": "Staten Island" },
        { "@type": "City", "name": "Bronx" }
      ],
      "openingHoursSpecification": [
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"],
          "opens": "07:00",
          "closes": "19:00"
        },
        {
          "@type": "OpeningHoursSpecification",
          "dayOfWeek": "Sunday",
          "opens": "00:00",
          "closes": "23:59",
          "description": "Emergency service only"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Electrical Services",
        "itemListElement": [
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Commercial Electrical Contracting" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Panel Upgrades & Breaker Replacement" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "EV Charger Installation" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Smart Home Wiring & Automation" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Emergency Electrical Services" } },
          { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Linear Lighting Installation & Maintenance" } }
        ]
      },
      "sameAs": [
        "https://linkedin.com",
        "https://facebook.com",
        "https://instagram.com"
      ]
    },
    {
      "@type": "WebSite",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#website`,
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "name": "Done to Power Up – DTPU",
      "publisher": { "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#business` }
    },
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#webpage`,
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "name": "DTPU – Licensed Electrician NYC & Westchester | Done to Power Up",
      "description": "Licensed electrical contractor serving NYC and Westchester County. 24/7 emergency service, panel upgrades, EV charger installation, commercial electrical contracting.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#website` },
      "about": { "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/#business` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL || "https://example.com" }
        ]
      }
    }
  ]
};

const HomePage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-home';
    script.textContent = JSON.stringify(HOME_SCHEMA);
    document.head.appendChild(script);
    document.title = 'DTPU – Licensed Electrician NYC & Westchester | Done to Power Up';
    return () => { document.getElementById('schema-home')?.remove(); };
  }, []);

  const trustBadges = [
    { icon: 'ri-shield-check-line', text: 'Licensed & Insured' },
    { icon: 'ri-24-hours-line', text: '24/7 Emergency' },
    { icon: 'ri-award-line', text: '15+ Years Experience' },
  ];

  const services = [
    {
      title: 'Commercial Electrical Contracting',
      description: 'Large-scale electrical installations &amp; demolition for retail spaces, supermarkets, and commercial buildings. Expert in piping &amp; EMT conduit systems, linear lighting systems and maintenance contracts.',
      icon: 'ri-building-line',
      badge: 'COMMERCIAL',
      anchor: 'commercial-services',
    },
    {
      title: 'Linear Lighting Installation & Maintenance',
      description: 'Professional installation and ongoing maintenance of linear lighting systems for supermarkets, retail stores, and commercial facilities.',
      icon: 'ri-lightbulb-flash-line',
      anchor: 'commercial-services',
    },
    {
      title: 'Panel Upgrades & Breaker Replacement',
      description: 'Upgrade outdated electrical panels to meet modern power demands. Safe, code-compliant installations for residential and commercial properties.',
      icon: 'ri-dashboard-line',
      anchor: 'residential-services',
    },
    {
      title: 'Smart Home Renovation & Automation Wiring',
      description: 'Professional wiring for smart home systems, automated lighting, security, and climate control.',
      icon: 'ri-home-wifi-line',
      anchor: 'residential-services',
    },
    {
      title: 'EV Charger Installation',
      description: 'Expert installation of electric vehicle charging stations for home and business. Fast, efficient charging solutions.',
      icon: 'ri-charging-pile-2-line',
      anchor: 'residential-services',
    },
    {
      title: 'Emergency Electrical Services',
      description: 'Available 24/7 for urgent electrical issues. Power outages, sparking outlets, and safety hazards resolved immediately.',
      icon: 'ri-alarm-warning-line',
      badge: '24/7 AVAILABLE',
      accent: 'red',
      anchor: 'residential-services',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/68039bea491764d51866727868c4153c.jpg"
            alt="Professional electricians at work"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>

        <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="flex justify-start">
            {/* Content Box - Left Aligned */}
            <div className="bg-white/95 backdrop-blur-sm p-12 rounded-2xl shadow-2xl max-w-xl text-center">
              <div className="flex items-center justify-center space-x-4 mb-6">
                <img 
                  src="https://static.readdy.ai/image/538b4e65f17508bb0aa3f7f0e8fac3a5/9515c99ad4768c15e42e5804f92636ee.jpeg" 
                  alt="Done to Power Up Logo" 
                  className="h-16 w-16 object-contain"
                />
                <h1 className="text-5xl font-extrabold text-secondary leading-tight">
                  DTPU
                </h1>
              </div>
              <p className="text-2xl font-serif text-secondary/80 mb-8">
                Powering Your Home & Business Safely
              </p>

              {/* Trust Badges */}
              <div className="flex flex-wrap justify-center gap-4 mb-10">
                {trustBadges.map((badge, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-2 px-4 py-2 border-2 border-primary rounded-full"
                  >
                    <i className={`${badge.icon} text-primary text-lg`}></i>
                    <span className="text-sm font-medium text-secondary whitespace-nowrap">
                      {badge.text}
                    </span>
                  </div>
                ))}
              </div>

              {/* CTA Buttons - Centered */}
              <div className="flex flex-col sm:flex-row justify-center gap-5">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center bg-primary text-white px-8 py-4 rounded-full font-semibold text-base hover:bg-primary/90 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                >
                  Request a Quote
                  <i className="ri-arrow-right-line ml-2 text-lg"></i>
                </Link>
                <a
                  href="tel:+16467501474"
                  className="inline-flex items-center justify-center border-2 border-primary text-primary px-8 py-4 rounded-full font-semibold text-base hover:bg-primary hover:text-white transition-all duration-300 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line mr-2 text-lg"></i>
                  Call Now: (646) 750-1474
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Floating Badge */}
        <div className="hidden lg:block absolute right-24 top-[50%] -translate-y-1/2 z-10">
          <Link
            to="/services"
            className="block bg-primary/90 backdrop-blur-sm text-white px-8 py-4 rounded-2xl shadow-2xl hover:bg-primary transition-all duration-300 cursor-pointer"
          >
            <p className="text-lg font-bold whitespace-nowrap">
              Same-Day Service Available
            </p>
          </Link>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          {/* Section Header */}
          <div className="text-center mb-16">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">
              OUR EXPERTISE
            </p>
            <h2 className="text-5xl font-bold text-secondary mb-6">
              Professional Electrical Contracting Services
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Specialized electrical contractors serving commercial and residential clients throughout NYC. From supermarket lighting installations to smart home wiring, we deliver safe, reliable, and code-compliant electrical solutions.
            </p>
          </div>

          {/* Services Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, index) => (
              <Link
                key={index}
                to={`/services#${service.anchor}`}
                className={`block bg-gray-50 p-8 rounded-xl border-l-4 ${
                  service.accent === 'red' ? 'border-red-500' : 'border-primary'
                } hover:shadow-lg hover:-translate-y-1 transition-all duration-300 relative cursor-pointer`}
              >
                {service.badge && (
                  <div className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    {service.badge}
                  </div>
                )}
                <div 
                  className="w-12 h-12 flex items-center justify-center mb-4 rounded-lg hover:bg-primary/10 transition-colors duration-300"
                >
                  <i className={`${service.icon} text-primary text-4xl`}></i>
                </div>
                <h3 className="text-xl font-bold text-secondary mb-3">
                  {service.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">
                  {service.description}
                </p>
                <span
                  className="inline-flex items-center text-primary font-medium text-sm hover:gap-2 transition-all duration-300"
                >
                  Learn More
                  <i className="ri-arrow-right-line ml-1"></i>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="bg-primary py-20">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-white text-4xl font-bold mb-3">
                Need Immediate Assistance?
              </h2>
              <p className="text-white/90 text-lg">
                Our emergency team is available 24/7 for urgent electrical issues
              </p>
            </div>
            <div className="flex flex-col sm:flex-row items-center gap-6">
              <a
                href="tel:+19145016880"
                className="text-white text-4xl font-bold hover:text-white/90 transition-colors duration-300 cursor-pointer whitespace-nowrap"
              >
                (914) 501-6880
              </a>
              <Link
                to="/contact"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                Schedule Service
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-secondary mb-6">
              Why Choose Done to Power Up?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We combine technical expertise with exceptional customer service to deliver electrical solutions you can trust.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: 'ri-shield-check-line',
                title: 'Licensed & Insured',
                description: 'Fully licensed electricians with comprehensive insurance coverage for your peace of mind.',
              },
              {
                icon: 'ri-time-line',
                title: 'Fast Response',
                description: 'Same-day service available. We arrive on time and complete work efficiently.',
              },
              {
                icon: 'ri-price-tag-3-line',
                title: 'Transparent Pricing',
                description: 'Upfront quotes with no hidden fees. You know the cost before we start.',
              },
              {
                icon: 'ri-customer-service-2-line',
                title: 'Expert Support',
                description: 'Friendly, knowledgeable team ready to answer questions and provide guidance.',
              },
            ].map((item, index) => (
              <div key={index} className="text-center">
                <Link 
                  to="/about"
                  className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-xl hover:bg-primary/10 transition-colors duration-300 cursor-pointer"
                >
                  <i className={`${item.icon} text-primary text-5xl`}></i>
                </Link>
                <h3 className="text-xl font-bold text-secondary mb-3">
                  {item.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hours of Service Section */}
      <section className="py-20 bg-white">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-primary text-xs font-bold tracking-[0.2em] uppercase mb-4">AVAILABILITY</p>
            <h2 className="text-4xl font-bold text-secondary mb-4">Hours of Service</h2>
            <p className="text-gray-600 text-base max-w-xl mx-auto">
              We're here when you need us — from routine work to around-the-clock emergencies.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {/* Monday - Saturday */}
            <div className="bg-gray-50 rounded-xl p-8 border-l-4 border-primary text-center hover:shadow-md transition-shadow duration-300">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-primary/10 rounded-full">
                <i className="ri-calendar-check-line text-primary text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-secondary mb-2">Monday – Saturday</h3>
              <p className="text-3xl font-extrabold text-primary mb-1">7AM – 7PM</p>
              <p className="text-sm text-gray-500">Regular Service Hours</p>
            </div>

            {/* Sunday */}
            <div className="bg-gray-50 rounded-xl p-8 border-l-4 border-red-500 text-center hover:shadow-md transition-shadow duration-300">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-red-50 rounded-full">
                <i className="ri-alarm-warning-line text-red-500 text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-secondary mb-2">Sunday</h3>
              <p className="text-3xl font-extrabold text-red-500 mb-1">24 Hours</p>
              <p className="text-sm text-gray-500">Emergency Only</p>
            </div>

            {/* Emergency CTA */}
            <div className="bg-secondary rounded-xl p-8 text-center hover:shadow-md transition-shadow duration-300 flex flex-col justify-center">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 bg-white/10 rounded-full">
                <i className="ri-phone-line text-white text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-white mb-2">Emergency Line</h3>
              <a
                href="tel:+19145016880"
                className="text-2xl font-extrabold text-primary hover:text-primary/80 transition-colors duration-300 whitespace-nowrap cursor-pointer"
              >
                (914) 501-6880
              </a>
              <p className="text-sm text-white/70 mt-1">Call anytime for emergencies</p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
